import { filter, type MonoTypeOperatorFunction } from 'rxjs';

import type { MarketSegment } from '@cosmos/types-common';

export const statDestinations = ['segment', 'asi-stats', 'appcues'] as const;
export type BaseStatDestination = (typeof statDestinations)[number];

type NegateType<Destination extends BaseStatDestination> = `!${Destination}`;
export type NegateStatDestination = NegateType<BaseStatDestination>;
export type StatDestination = BaseStatDestination | NegateStatDestination;

export type TrackEventMobileProps = Pick<
  TrackEventData,
  'appVersion' | 'appPlatform' | 'nativeApp'
>;
export interface TrackEventData {
  appCode?: string;
  appId?: number;
  pageUrlName?: string;
  referrerPageUrlName?: string;
  nativeApp?: boolean;
  appPlatform?: string;
  appVersion?: string;
  asiGuid?: string;

  // the following props are being set for storefront analytics only
  botscore?: string;
  ipcountry?: string;
  ja4?: string;
  cfrayid?: string;
}

export interface TrackerProperties<T, U = unknown> {
  eventProperties?: Partial<T>;
  extras?: U;
  statDestination?: StatDestination[];
}

const isDestinationEnabled = (
  destinationToCheck: StatDestination,
  event: TrackEvent<TrackEventData>
) => {
  const statDestinations = event.statDestination!;

  const hasNegateFlags = statDestinations.some((it) => it[0] === '!');
  if (hasNegateFlags) {
    return (
      !statDestinations.some((it) => it.slice(1) === destinationToCheck) ||
      false
    );
  }

  return statDestinations.some((it) => it === destinationToCheck);
};

export const isDestinationEnabled$ = (
  // TODO(ds): array signature is needed temporary while we migrate segment to webhooks
  destinationToCheck: StatDestination | StatDestination[],
  options?: {
    // If true, the event will not be recorded if no statDestination is specified
    strict: boolean;
  }
): MonoTypeOperatorFunction<TrackEvent<TrackEventData>> => {
  return filter((event: TrackEvent<TrackEventData>) => {
    if (event.statDestination?.length) {
      return Array.isArray(destinationToCheck)
        ? destinationToCheck.some((d) => isDestinationEnabled(d, event))
        : isDestinationEnabled(destinationToCheck, event);
    }

    return !options?.strict;
  });
};

export interface TrackEvent<T extends TrackEventData> {
  action: string;
  properties: T;

  /**
   * If this is empty, the stats will be recorded to all registered stat destinations, by default.
   * As of now, the available ones are 'asi-stats' and 'segment'
   */
  statDestination?: StatDestination[];
}

/**
 * Creates strongly typed events with constant action name
 *
 * @example
 * ```
 * export type DecoratorDetailViewedEvent = TypedTrackEvent<
 *   'Decorator Detail Viewed',
 *   {
 *     id: number;
 *     pageNumber: number;
 *   }
 * >;
 * ```
 */
export type TypedTrackEvent<ACTION extends string, T> = TrackEvent<
  T & TrackEventData
> & { action: ACTION };

export interface SearchResultFeedbackProperties extends TrackEventData {
  sentiment: 'positive' | 'negative';
  reasons?: string[];
  additionalDetails?: string;
  term?: string;
  search?: string;
  queryID?: string;
  pageNumber?: number;
  marketSegmentCode?: MarketSegment;
  searchType: 'Product' | 'Supplier' | 'Decorator';
  logRocketSessionUrl?: string | null;
}

export type SearchResultFeedbackEvent = TypedTrackEvent<
  'Search Feedback Submitted',
  SearchResultFeedbackProperties
>;
